/**
 * RiskTable — DataTable + TableToolbar wrapper for Step 6.
 *
 * `treatment_decision` is the filter dimension (4 multi-select
 * options via FilterDrawer). Add / Edit / Delete + CSV slots
 * mirror the Step 4 / Step 5 shape.
 *
 * Synthetic `_rowKey` — child TARAs can collide on risk_id.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
  RowActionsMenu,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import {
  RISK_COLUMNS_READ,
  RISK_COLUMNS_EDIT,
  RISK_ACTIONS_COLUMN_WIDTH,
  TREATMENT_DECISIONS,
  TREATMENT_PRIORITIES,
  RISK_VALUE_OPTIONS,
  type RiskRecord,
} from './riskFieldSchema';
import styles from './RiskTable.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const RowActionsMenuAny = RowActionsMenu as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;
// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

export interface RiskTableProps {
  risks: RiskRecord[];
  loading?: boolean;
  emptyMessage?: string;
  editable?: boolean;
  onAdd?: () => void;
  onEdit?: (risk: RiskRecord) => void;
  onDelete?: (risk: RiskRecord) => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
}

function sortRisks(rows: RiskRecord[], sort: SortState | null): RiskRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchRisks(rows: RiskRecord[], query: string): RiskRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function RiskTable({
  risks,
  loading,
  emptyMessage = 'No risks assessed yet.',
  editable = false,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
}: RiskTableProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [decisionValues, setDecisionValues] = useState<string[]>([]);
  const [riskValueValues, setRiskValueValues] = useState<string[]>([]);
  const [priorityValues, setPriorityValues] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const decisionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of TREATMENT_DECISIONS) counts[d.value] = 0;
    for (const r of risks) {
      const k = String(r.treatment_decision ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [risks]);

  // risk_value is numeric (1-5); the drawer works in strings, so count +
  // match on the stringified value.
  const riskValueCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const o of RISK_VALUE_OPTIONS) counts[String(o.value)] = 0;
    for (const r of risks) {
      const k = String(r.risk_value ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [risks]);

  const priorityCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of TREATMENT_PRIORITIES) counts[p.value] = 0;
    for (const r of risks) {
      const k = String(r.treatment_priority ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [risks]);

  const decisionFiltered = useMemo(
    () => (decisionValues.length === 0
      ? risks
      : risks.filter((r) => decisionValues.includes(String(r.treatment_decision ?? '')))),
    [risks, decisionValues],
  );
  const riskValueFiltered = useMemo(
    () => (riskValueValues.length === 0
      ? decisionFiltered
      : decisionFiltered.filter((r) => riskValueValues.includes(String(r.risk_value ?? '')))),
    [decisionFiltered, riskValueValues],
  );
  const priorityFiltered = useMemo(
    () => (priorityValues.length === 0
      ? riskValueFiltered
      : riskValueFiltered.filter((r) => priorityValues.includes(String(r.treatment_priority ?? '')))),
    [riskValueFiltered, priorityValues],
  );
  const searched = useMemo(() => searchRisks(priorityFiltered, search), [priorityFiltered, search]);
  const sorted = useMemo(() => sortRisks(searched, sort), [searched, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.risk_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(() => {
    if (!editable) return RISK_COLUMNS_READ;
    return [
      ...RISK_COLUMNS_EDIT,
      {
        key: '_actions',
        label: '',
        align: 'right',
        width: RISK_ACTIONS_COLUMN_WIDTH,
        sortable: false,
        render: (row: RiskRecord) => (
          <RowActionsMenuAny
            ariaLabel={`Actions for ${row.risk_id}`}
            actions={[
              { id: 'edit', label: 'Edit', icon: <Icon name="edit" size={14} />, onClick: () => onEdit?.(row) },
              { id: 'delete', label: 'Delete', icon: <Icon name="delete" size={14} />, danger: true, onClick: () => onDelete?.(row) },
            ]}
          />
        ),
      },
    ];
  }, [editable, onEdit, onDelete]);

  const decisionFilter = {
    key: 'treatment_decision',
    label: 'Treatment',
    values: decisionValues,
    onChange: (next: string[]) => {
      setDecisionValues(next);
      setPage(1);
    },
    options: TREATMENT_DECISIONS.map((d) => ({
      value: d.value,
      label: d.label,
      count: decisionCounts[d.value] ?? 0,
    })),
  };

  const riskValueFilter = {
    key: 'risk_value',
    label: 'Risk Value',
    values: riskValueValues,
    onChange: (next: string[]) => {
      setRiskValueValues(next);
      setPage(1);
    },
    options: RISK_VALUE_OPTIONS.map((o) => ({
      value: String(o.value),
      label: o.label,
      count: riskValueCounts[String(o.value)] ?? 0,
    })),
  };

  const priorityFilter = {
    key: 'treatment_priority',
    label: 'Priority',
    values: priorityValues,
    onChange: (next: string[]) => {
      setPriorityValues(next);
      setPage(1);
    },
    options: TREATMENT_PRIORITIES.map((p) => ({
      value: p.value,
      label: p.label,
      count: priorityCounts[p.value] ?? 0,
    })),
  };

  const toolbarActions = (
    <div className={styles.toolbarActions}>
      {onExport && sorted.length > 0 && (
        <ButtonAny variant="outline" size="sm" onClick={onExport}>
          <Icon name="upload" size={14} /> Export CSV
        </ButtonAny>
      )}
      {editable && onImport && (
        <>
          <ButtonAny
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="download" size={14} /> Import
          </ButtonAny>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImport(f);
              if (e.target) e.target.value = '';
            }}
          />
        </>
      )}
      {editable && onAdd && (
        <ButtonAny variant="default" size="sm" onClick={onAdd}>
          <Icon name="add" size={14} /> Add Risk
        </ButtonAny>
      )}
    </div>
  );

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[decisionFilter, riskValueFilter, priorityFilter]}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        sort={sort}
        onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
          setSort(column && direction ? { column, direction } : null)
        }
        loading={loading}
        emptyMessage={emptyMessage}
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => {
                setSearch(v);
                setPage(1);
              },
              placeholder: 'Search risks…',
            }}
            count={{ value: sorted.length, label: 'Risks' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || decisionValues.length > 0 || riskValueValues.length > 0 || priorityValues.length > 0}
            actions={toolbarActions}
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={sorted.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
